/**
 * Universal Web Tracking SDK v1.0
 * Automatically tracks: Clicks, Heatmaps, Products, Sessions, Browser Data, IP, Events
 * Can be added to ANY web application
 */

(function(window, document) {
  'use strict';

  // Configuration
  const CONFIG = {
    apiEndpoint: 'https://cvm-api.worktual.co.uk/cvm_api_service/v1/insert_tracking_nfspath',
    domainId: 20556,
    domainName: window.location.hostname,
    autoTrack: true,
    trackProducts: true,
    trackHeatmap: true,
    trackSession: true,
    batchInterval: 60000, // Send metrics every 60 seconds
    debug: false,
  };

  // Tracking state
  const STATE = {
    visitorId: null,
    sessionId: null,
    pageLoadTime: Date.now(),
    customerDetails: {
      email: null,
      mobile: null,
      name: null,
      firstName: null,
      lastName: null,
      customerId: null
    },
    metrics: {
      Heap_Tracking: { name: 'Heap_Tracking', status: 'success', data: {}, lastUpdate: 0, count: 0 },
      Heatmap_Tracking: { name: 'Heatmap_Tracking', status: 'success', data: { clicks: [], moves: [], scrolls: [], rageClicks: [], deadClicks: [] }, lastUpdate: 0, count: 0 },
      Product_Click_Tracking: { name: 'Product_Click_Tracking', status: 'success', data: { clicks: [] }, metadata: {}, lastUpdate: 0, count: 0 },
      Visitor_ID: { name: 'Visitor_ID', status: 'success', data: {}, lastUpdate: 0, count: 0 },
      Browser_Fingerprint: { name: 'Browser_Fingerprint', status: 'success', data: {}, lastUpdate: 0, count: 0 },
      Referrer_Info: { name: 'Referrer_Info', status: 'success', data: {}, lastUpdate: 0, count: 0 },
      IP_Tracking: { name: 'IP_Tracking', status: 'success', data: {}, lastUpdate: 0, count: 0 },
      Event_Tracking: { name: 'Event_Tracking', status: 'success', data: {}, lastUpdate: 0, count: 0 },
      Pixel_Tracking: { name: 'Pixel_Tracking', status: 'success', data: {}, lastUpdate: 0, count: 0 },
      Cookie_Operations: { name: 'Cookie_Operations', status: 'success', data: {}, lastUpdate: 0, count: 0 },
    },
    batchTimer: null,
    // Rage click detection state
    clickHistory: [],
    lastClickElement: null,
  };

  // Utility: Generate unique ID
  function generateId(prefix) {
    return prefix + '_' + Math.random().toString(36).substring(2, 11) + Date.now().toString(36);
  }

  // Utility: Get/Set Cookie
  function getCookie(name) {
    const cookies = document.cookie ? document.cookie.split('; ') : [];
    const cookie = cookies.find(row => row.startsWith(name + '='));
    return cookie ? decodeURIComponent(cookie.split('=')[1]) : null;
  }

  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; expires=' + expires + '; SameSite=Lax';
  }

  // Utility: Check consent
  function isConsentMissing() {
    const consent = getCookie('CK_Cons') || localStorage.getItem('cookieConsent');
    return !consent;
  }

  // Initialize Visitor ID
  function initVisitorId() {
    let visitorId = getCookie('visitor_id');
    if (!visitorId) {
      visitorId = generateId('visitor');
      setCookie('visitor_id', visitorId, 365);
      trackCookieOperation('set', 'visitor_id', visitorId, 365);
    } else {
      trackCookieOperation('get', 'visitor_id', visitorId);
    }
    STATE.visitorId = visitorId;

    STATE.metrics.Visitor_ID.data = {
      visitorId: visitorId,
      timestamp: Date.now()
    };
    STATE.metrics.Visitor_ID.lastUpdate = Date.now();
    STATE.metrics.Visitor_ID.count = 1;
  }

  // Initialize Session ID
  function initSessionId() {
    let sessionId = sessionStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = generateId('session');
      sessionStorage.setItem('session_id', sessionId);
    }
    STATE.sessionId = sessionId;
  }

  // Initialize Customer Details - Shopify Integration
  function initCustomerDetails() {
    // Try to get customer data from Shopify's global objects
    const customer = getShopifyCustomer();
    if (customer) {
      updateCustomerDetails(customer);
    }

    // Also check for stored customer data
    const storedCustomer = localStorage.getItem('wt_customer_details');
    if (storedCustomer && !STATE.customerDetails.email) {
      try {
        const parsed = JSON.parse(storedCustomer);
        updateCustomerDetails(parsed);
      } catch (e) {
        if (CONFIG.debug) console.warn('Failed to parse stored customer data:', e);
      }
    }

    // Listen for Shopify customer login events
    listenForCustomerChanges();
  }

  // Get Shopify Customer Data
  function getShopifyCustomer() {
    const customer = {};

    // Method 1: Shopify's __st object (Shopify Analytics)
    if (window.__st && window.__st.cid) {
      customer.customerId = window.__st.cid;
    }

    // Method 2: Shopify's ShopifyAnalytics object
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page) {
      const meta = window.ShopifyAnalytics.meta;
      if (meta.page.customerId) {
        customer.customerId = meta.page.customerId;
      }
    }

    // Method 3: Shopify's meta tags
    const customerIdMeta = document.querySelector('meta[name="shopify-customer-id"]');
    if (customerIdMeta) {
      customer.customerId = customerIdMeta.getAttribute('content');
    }

    // Method 4: Check Shopify's window.customer object (if available via theme)
    if (window.customer) {
      if (window.customer.email) customer.email = window.customer.email;
      if (window.customer.phone) customer.mobile = window.customer.phone;
      if (window.customer.first_name) customer.firstName = window.customer.first_name;
      if (window.customer.last_name) customer.lastName = window.customer.last_name;
      if (window.customer.name) customer.name = window.customer.name;
      if (window.customer.id) customer.customerId = window.customer.id;
    }

    // Method 5: Check for Shopify Liquid-injected customer data
    if (window.__SHOPIFY_CUSTOMER__) {
      const sc = window.__SHOPIFY_CUSTOMER__;
      if (sc.email) customer.email = sc.email;
      if (sc.phone) customer.mobile = sc.phone;
      if (sc.firstName || sc.first_name) customer.firstName = sc.firstName || sc.first_name;
      if (sc.lastName || sc.last_name) customer.lastName = sc.lastName || sc.last_name;
      if (sc.id) customer.customerId = sc.id;
    }

    // Method 6: Parse customer data from data attributes on body or specific elements
    const customerDataEl = document.querySelector('[data-customer-email]');
    if (customerDataEl) {
      customer.email = customerDataEl.getAttribute('data-customer-email') || customer.email;
      customer.mobile = customerDataEl.getAttribute('data-customer-phone') || customer.mobile;
      customer.firstName = customerDataEl.getAttribute('data-customer-first-name') || customer.firstName;
      customer.lastName = customerDataEl.getAttribute('data-customer-last-name') || customer.lastName;
    }

    // Build full name if we have first/last but no full name
    if (!customer.name && (customer.firstName || customer.lastName)) {
      customer.name = [customer.firstName, customer.lastName].filter(Boolean).join(' ');
    }

    return Object.keys(customer).length > 0 ? customer : null;
  }

  // Update Customer Details
  function updateCustomerDetails(details) {
    if (!details) return;

    if (details.email) STATE.customerDetails.email = details.email;
    if (details.mobile || details.phone) STATE.customerDetails.mobile = details.mobile || details.phone;
    if (details.name) STATE.customerDetails.name = details.name;
    if (details.firstName || details.first_name) STATE.customerDetails.firstName = details.firstName || details.first_name;
    if (details.lastName || details.last_name) STATE.customerDetails.lastName = details.lastName || details.last_name;
    if (details.customerId || details.id) STATE.customerDetails.customerId = details.customerId || details.id;

    // Build full name if not provided
    if (!STATE.customerDetails.name && (STATE.customerDetails.firstName || STATE.customerDetails.lastName)) {
      STATE.customerDetails.name = [STATE.customerDetails.firstName, STATE.customerDetails.lastName].filter(Boolean).join(' ');
    }

    // Persist customer details
    localStorage.setItem('wt_customer_details', JSON.stringify(STATE.customerDetails));

    if (CONFIG.debug) console.log('Customer details updated:', STATE.customerDetails);
  }

  // Listen for customer changes (login/logout, form submissions)
  function listenForCustomerChanges() {
    // Monitor for Shopify customer login via page reload detection
    const checkInterval = setInterval(() => {
      const customer = getShopifyCustomer();
      if (customer && customer.email && !STATE.customerDetails.email) {
        updateCustomerDetails(customer);
        clearInterval(checkInterval);
      }
    }, 2000);

    // Stop checking after 30 seconds
    setTimeout(() => clearInterval(checkInterval), 30000);

    // Capture customer details from form submissions
    document.addEventListener('submit', function(e) {
      const form = e.target;
      captureFormCustomerDetails(form);
    });

    // Capture from input blur events on email/phone fields
    document.addEventListener('blur', function(e) {
      const target = e.target;
      if (target.tagName === 'INPUT') {
        captureInputCustomerDetails(target);
      }
    }, true);
  }

  // Capture customer details from forms
  function captureFormCustomerDetails(form) {
    const formData = {};

    // Email fields
    const emailInput = form.querySelector('input[type="email"], input[name*="email"], input[id*="email"]');
    if (emailInput && emailInput.value) {
      formData.email = emailInput.value;
    }

    // Phone fields
    const phoneInput = form.querySelector('input[type="tel"], input[name*="phone"], input[name*="mobile"], input[id*="phone"], input[id*="mobile"]');
    if (phoneInput && phoneInput.value) {
      formData.mobile = phoneInput.value;
    }

    // Name fields
    const firstNameInput = form.querySelector('input[name*="first_name"], input[name*="firstName"], input[id*="first_name"], input[id*="firstName"]');
    const lastNameInput = form.querySelector('input[name*="last_name"], input[name*="lastName"], input[id*="last_name"], input[id*="lastName"]');
    const nameInput = form.querySelector('input[name*="name"]:not([name*="first"]):not([name*="last"]), input[id*="name"]:not([id*="first"]):not([id*="last"])');

    if (firstNameInput && firstNameInput.value) formData.firstName = firstNameInput.value;
    if (lastNameInput && lastNameInput.value) formData.lastName = lastNameInput.value;
    if (nameInput && nameInput.value && !formData.firstName) formData.name = nameInput.value;

    if (Object.keys(formData).length > 0) {
      updateCustomerDetails(formData);
    }
  }

  // Capture customer details from individual input fields
  function captureInputCustomerDetails(input) {
    const name = (input.name || '').toLowerCase();
    const id = (input.id || '').toLowerCase();
    const type = input.type;
    const value = input.value;

    if (!value) return;

    const formData = {};

    // Email detection
    if (type === 'email' || name.includes('email') || id.includes('email')) {
      if (isValidEmail(value)) {
        formData.email = value;
      }
    }

    // Phone detection
    if (type === 'tel' || name.includes('phone') || name.includes('mobile') || id.includes('phone') || id.includes('mobile')) {
      formData.mobile = value;
    }

    // Name detection
    if (name.includes('first_name') || name.includes('firstname') || id.includes('first_name') || id.includes('firstname')) {
      formData.firstName = value;
    }
    if (name.includes('last_name') || name.includes('lastname') || id.includes('last_name') || id.includes('lastname')) {
      formData.lastName = value;
    }

    if (Object.keys(formData).length > 0) {
      updateCustomerDetails(formData);
    }
  }

  // Validate email format
  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // Get current customer details (for inclusion in events)
  function getCustomerDetails() {
    const details = {};
    if (STATE.customerDetails.email) details.email = STATE.customerDetails.email;
    if (STATE.customerDetails.mobile) details.mobile = STATE.customerDetails.mobile;
    if (STATE.customerDetails.name) details.name = STATE.customerDetails.name;
    if (STATE.customerDetails.firstName) details.firstName = STATE.customerDetails.firstName;
    if (STATE.customerDetails.lastName) details.lastName = STATE.customerDetails.lastName;
    if (STATE.customerDetails.customerId) details.customerId = STATE.customerDetails.customerId;
    return Object.keys(details).length > 0 ? details : null;
  }

  // Browser Fingerprinting
  function getBrowserFingerprint() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Browser fingerprint', 2, 2);
    }

    const fingerprint = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      hardwareConcurrency: navigator.hardwareConcurrency,
      maxTouchPoints: navigator.maxTouchPoints,
      screen: {
        width: screen.width,
        height: screen.height,
        colorDepth: screen.colorDepth,
        pixelDepth: screen.pixelDepth,
        availWidth: screen.availWidth,
        availHeight: screen.availHeight
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset(),
      canvas: canvas.toDataURL(),
      localStorage: !!window.localStorage,
      sessionStorage: !!window.sessionStorage,
      indexedDB: !!window.indexedDB,
    };

    // Add connection info if available
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      fingerprint.connection = {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt
      };
    }

    STATE.metrics.Browser_Fingerprint.data = fingerprint;
    STATE.metrics.Browser_Fingerprint.lastUpdate = Date.now();
    STATE.metrics.Browser_Fingerprint.count = 1;

    return fingerprint;
  }

  // Get Referrer Info
  function getReferrerInfo() {
    const referrer = document.referrer;
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams = {};

    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
      const value = urlParams.get(param);
      if (value) utmParams[param] = value;
    });

    const info = {
      referrer: referrer || 'direct',
      currentUrl: window.location.href,
      utmParams: utmParams,
      searchParams: Object.fromEntries(urlParams.entries()),
      isExternalReferrer: referrer && !referrer.includes(window.location.hostname),
      referrerDomain: referrer ? new URL(referrer).hostname : null
    };

    STATE.metrics.Referrer_Info.data = info;
    STATE.metrics.Referrer_Info.lastUpdate = Date.now();
    STATE.metrics.Referrer_Info.count = 1;

    return info;
  }

  // Track IP Address with Geolocation
  async function trackIP() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      const ip = data.ip;

      // Try to get location data
      try {
        const locationResponse = await fetch(`https://ipapi.co/${ip}/json/`);
        const locationData = await locationResponse.json();

        STATE.metrics.IP_Tracking.data = {
          ip: ip,
          timestamp: Date.now(),
          location: {
            city: locationData.city,
            region: locationData.region,
            country: locationData.country_name,
            countryCode: locationData.country_code,
            latitude: locationData.latitude,
            longitude: locationData.longitude,
            timezone: locationData.timezone,
            isp: locationData.org
          }
        };
      } catch (locError) {
        // Fallback without location
        STATE.metrics.IP_Tracking.data = {
          ip: ip,
          timestamp: Date.now()
        };
      }

      STATE.metrics.IP_Tracking.lastUpdate = Date.now();
      STATE.metrics.IP_Tracking.count = 1;
    } catch (error) {
      if (CONFIG.debug) console.warn('IP tracking failed:', error);
    }
  }

  // Product Click Tracking
  function trackProductClick(event) {
    const target = event.target;
    const productElement = target.closest('[data-product-id]');

    if (!productElement) return;

    const productId = productElement.getAttribute('data-product-id') || '';
    const productData = {
      productId: productId,
      productName: productElement.getAttribute('data-product-name') || '',
      productCategory: productElement.getAttribute('data-product-category') || '',
      productPrice: parseFloat(productElement.getAttribute('data-product-price') || '0'),
      productCurrency: productElement.getAttribute('data-product-currency') || 'USD',
      productSKU: productElement.getAttribute('data-product-sku') || undefined,
      productBrand: productElement.getAttribute('data-product-brand') || undefined,
      productImage: productElement.querySelector('img')?.src || '',
      productUrl: window.location.href,
      productAvailability: productElement.getAttribute('data-product-availability') || 'in_stock',

      clickPosition: { x: event.clientX, y: event.clientY },
      elementClicked: getElementType(target),
      section: productElement.getAttribute('data-section') || productElement.getAttribute('data-track-section') || 'unknown',
      pageContext: window.location.pathname,

      timestamp: Date.now(),
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      timeOnPage: Date.now() - STATE.pageLoadTime,
      scrollDepth: getScrollDepth(),
      customerDetails: getCustomerDetails()
    };

    STATE.metrics.Product_Click_Tracking.data.clicks.push(productData);
    STATE.metrics.Product_Click_Tracking.lastUpdate = Date.now();
    STATE.metrics.Product_Click_Tracking.count = STATE.metrics.Product_Click_Tracking.data.clicks.length;
    STATE.metrics.Product_Click_Tracking.metadata = {
      pageType: getPageType(),
      totalProductsViewed: STATE.metrics.Product_Click_Tracking.data.clicks.length
    };

    // Track as pixel/conversion event
    trackPixelEvent('product_click', productId);

    // Track as custom event
    if (!STATE.metrics.Event_Tracking.data.events) {
      STATE.metrics.Event_Tracking.data.events = [];
    }
    STATE.metrics.Event_Tracking.data.events.push({
      action: 'product_clicked',
      timestamp: Date.now(),
      page: window.location.pathname,
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails(),
      data: {
        productId: productId,
        productName: productData.productName,
        section: productData.section
      }
    });
    STATE.metrics.Event_Tracking.lastUpdate = Date.now();
    STATE.metrics.Event_Tracking.count = STATE.metrics.Event_Tracking.data.events.length;

    if (CONFIG.debug) console.log('Product click tracked:', productData);
  }

  // Heatmap Tracking
  function trackClick(event) {
    const clickData = {
      x: event.clientX,
      y: event.clientY,
      type: 'click',
      timestamp: Date.now(),
      element: getElementSelector(event.target),
      elementType: event.target.tagName.toLowerCase(),
      pageX: event.pageX,
      pageY: event.pageY,
      page: window.location.pathname,
      url: window.location.href,
      screenWidth: screen.width,
      screenHeight: screen.height,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails()
    };

    STATE.metrics.Heatmap_Tracking.data.clicks.push(clickData);
    STATE.metrics.Heatmap_Tracking.lastUpdate = Date.now();
    STATE.metrics.Heatmap_Tracking.count = STATE.metrics.Heatmap_Tracking.data.clicks.length;
  }

  // Track Mouse Movement (sampled)
  let mouseMoveThrottle = 0;
  function trackMouseMove(event) {
    const now = Date.now();
    if (now - mouseMoveThrottle < 200) return; // Throttle to every 200ms
    mouseMoveThrottle = now;

    const moveData = {
      x: event.clientX,
      y: event.clientY,
      type: 'move',
      timestamp: now,
      element: getElementSelector(event.target),
      elementType: event.target.tagName.toLowerCase(),
      value: Math.random() * 100, // Intensity value
      pageX: event.pageX,
      pageY: event.pageY,
      page: window.location.pathname,
      url: window.location.href,
      screenWidth: screen.width,
      screenHeight: screen.height,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      sessionId: STATE.sessionId
    };

    STATE.metrics.Heatmap_Tracking.data.moves.push(moveData);

    // Keep only last 100 moves to prevent memory issues
    if (STATE.metrics.Heatmap_Tracking.data.moves.length > 100) {
      STATE.metrics.Heatmap_Tracking.data.moves.shift();
    }

    STATE.metrics.Heatmap_Tracking.lastUpdate = now;
    STATE.metrics.Heatmap_Tracking.count = STATE.metrics.Heatmap_Tracking.data.moves.length;
  }

  // Track Scroll
  let scrollThrottle = 0;
  function trackScroll() {
    const now = Date.now();
    if (now - scrollThrottle < 500) return; // Throttle to every 500ms
    scrollThrottle = now;

    const scrollData = {
      x: window.scrollX,
      y: window.scrollY,
      type: 'scroll',
      timestamp: now,
      depth: getScrollDepth(),
      page: window.location.pathname,
      sessionId: STATE.sessionId
    };

    STATE.metrics.Heatmap_Tracking.data.scrolls.push(scrollData);

    // Keep only last 50 scrolls
    if (STATE.metrics.Heatmap_Tracking.data.scrolls.length > 50) {
      STATE.metrics.Heatmap_Tracking.data.scrolls.shift();
    }
  }

  // Rage Click Detection - Detects rapid repeated clicks indicating user frustration
  function detectRageClick(event) {
    const now = Date.now();
    const target = event.target;
    const selector = getElementSelector(target);

    // Add current click to history
    STATE.clickHistory.push({
      timestamp: now,
      x: event.clientX,
      y: event.clientY,
      element: selector
    });

    // Keep only clicks from last 2 seconds
    STATE.clickHistory = STATE.clickHistory.filter(click => now - click.timestamp < 2000);

    // Rage click: 3+ clicks within 2 seconds in similar area (within 100px)
    if (STATE.clickHistory.length >= 3) {
      const recentClicks = STATE.clickHistory.slice(-3);
      const firstClick = recentClicks[0];
      const allNearby = recentClicks.every(click =>
        Math.abs(click.x - firstClick.x) < 100 &&
        Math.abs(click.y - firstClick.y) < 100
      );

      if (allNearby) {
        const rageClickData = {
          type: 'rage_click',
          timestamp: now,
          clickCount: STATE.clickHistory.length,
          duration: now - STATE.clickHistory[0].timestamp,
          x: event.clientX,
          y: event.clientY,
          pageX: event.pageX,
          pageY: event.pageY,
          element: selector,
          elementType: target.tagName.toLowerCase(),
          elementText: (target.textContent || '').substring(0, 50).trim(),
          page: window.location.pathname,
          url: window.location.href,
          sessionId: STATE.sessionId,
          visitorId: STATE.visitorId,
          customerDetails: getCustomerDetails()
        };

        STATE.metrics.Heatmap_Tracking.data.rageClicks.push(rageClickData);
        STATE.metrics.Heatmap_Tracking.lastUpdate = now;

        // Keep only last 20 rage clicks
        if (STATE.metrics.Heatmap_Tracking.data.rageClicks.length > 20) {
          STATE.metrics.Heatmap_Tracking.data.rageClicks.shift();
        }

        // Clear click history after detecting rage click
        STATE.clickHistory = [];

        if (CONFIG.debug) console.log('Rage click detected:', rageClickData);
      }
    }
  }

  // Dead Click Detection - Detects clicks that produce no response
  function detectDeadClick(event) {
    const target = event.target;
    const selector = getElementSelector(target);
    const tagName = target.tagName.toLowerCase();

    // Elements that should respond to clicks
    const interactiveElements = ['a', 'button', 'input', 'select', 'textarea', 'label'];
    const hasClickHandler = target.onclick || target.hasAttribute('onclick');
    const isInteractive = interactiveElements.includes(tagName) ||
                          target.hasAttribute('role') ||
                          target.hasAttribute('tabindex') ||
                          target.closest('a, button') ||
                          hasClickHandler;

    // Skip if element is clearly interactive
    if (isInteractive) return;

    // Check if element looks clickable (has cursor pointer style)
    const computedStyle = window.getComputedStyle(target);
    const looksClickable = computedStyle.cursor === 'pointer';

    // Dead click: Element looks clickable but isn't interactive
    if (looksClickable) {
      const deadClickData = {
        type: 'dead_click',
        timestamp: Date.now(),
        x: event.clientX,
        y: event.clientY,
        pageX: event.pageX,
        pageY: event.pageY,
        element: selector,
        elementType: tagName,
        elementText: (target.textContent || '').substring(0, 50).trim(),
        computedCursor: computedStyle.cursor,
        page: window.location.pathname,
        url: window.location.href,
        sessionId: STATE.sessionId,
        visitorId: STATE.visitorId,
        customerDetails: getCustomerDetails()
      };

      STATE.metrics.Heatmap_Tracking.data.deadClicks.push(deadClickData);
      STATE.metrics.Heatmap_Tracking.lastUpdate = Date.now();

      // Keep only last 20 dead clicks
      if (STATE.metrics.Heatmap_Tracking.data.deadClicks.length > 20) {
        STATE.metrics.Heatmap_Tracking.data.deadClicks.shift();
      }

      if (CONFIG.debug) console.log('Dead click detected:', deadClickData);
    }
  }

  // Helper: Get element selector
  function getElementSelector(element) {
    if (!element) return '';
    if (element.id) return '#' + element.id;
    if (element.className && typeof element.className === 'string') {
      const classes = element.className.trim().split(/\s+/).slice(0, 2).join('.');
      return classes ? '.' + classes : element.tagName.toLowerCase();
    }
    return element.tagName.toLowerCase();
  }

  // Helper: Get element type
  function getElementType(element) {
    if (element.tagName === 'IMG') return 'image';
    if (element.tagName === 'BUTTON') return 'button';
    if (element.tagName === 'A') return 'link';
    if (/^H[1-6]$/.test(element.tagName)) return 'heading';
    return 'element';
  }

  // Helper: Get scroll depth
  function getScrollDepth() {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    return docHeight > 0 ? Math.round((scrollTop / docHeight) * 100 * 100) / 100 : 0;
  }

  // Helper: Get page type
  function getPageType() {
    const path = window.location.pathname;
    if (path.includes('/product/')) return 'product_detail';
    if (path.includes('/category/')) return 'category';
    if (path.includes('/search')) return 'search';
    if (path === '/') return 'homepage';
    return 'other';
  }

  // Track Heap Memory (if available)
  function trackHeapMemory() {
    if (performance.memory) {
      const usedMB = (performance.memory.usedJSHeapSize / 1048576);
      const totalMB = (performance.memory.totalJSHeapSize / 1048576);
      const limitMB = (performance.memory.jsHeapSizeLimit / 1048576);
      const usedPercentage = (performance.memory.usedJSHeapSize / performance.memory.totalJSHeapSize) * 100;

      const heap = {
        usedJSHeapSize: performance.memory.usedJSHeapSize,
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
        usedJSHeapSizeMB: usedMB.toFixed(2),
        totalJSHeapSizeMB: totalMB.toFixed(2),
        jsHeapSizeLimitMB: limitMB.toFixed(2),
        usedPercentage: usedPercentage.toFixed(2),
        availableHeapMB: (limitMB - usedMB).toFixed(2),
        formatted: {
          used: usedMB.toFixed(2) + ' MB',
          total: totalMB.toFixed(2) + ' MB',
          limit: limitMB.toFixed(2) + ' MB',
          percentage: usedPercentage.toFixed(2) + '%'
        }
      };

      STATE.metrics.Heap_Tracking.data = {
        type: 'heap_memory',
        timestamp: Date.now(),
        page: window.location.pathname,
        heap: heap,
        browser: navigator.userAgent,
        source: 'webtracking_sdk',
        heatmapData: STATE.metrics.Heatmap_Tracking.data,
        Click_Tracking: STATE.metrics.Product_Click_Tracking
      };
      STATE.metrics.Heap_Tracking.lastUpdate = Date.now();
      STATE.metrics.Heap_Tracking.count = 1;
    }
  }

  // Track Page View Event
  function trackPageView() {
    const eventData = {
      action: 'page_view',
      timestamp: Date.now(),
      page: window.location.pathname,
      url: window.location.href,
      source: 'webtracking_sdk',
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails(),
      metadata: {
        referrer: document.referrer || 'direct',
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        title: document.title,
        url: window.location.href
      }
    };

    if (!STATE.metrics.Event_Tracking.data.events) {
      STATE.metrics.Event_Tracking.data.events = [];
    }
    STATE.metrics.Event_Tracking.data.events.push(eventData);
    STATE.metrics.Event_Tracking.lastUpdate = Date.now();
    STATE.metrics.Event_Tracking.count = STATE.metrics.Event_Tracking.data.events.length;
  }

  // Track Pixel/Conversion Events
  function trackPixelEvent(type, productId = null) {
    if (!STATE.metrics.Pixel_Tracking.data.conversions) {
      STATE.metrics.Pixel_Tracking.data.conversions = [];
    }

    const conversion = {
      type: type,
      value: 1,
      timestamp: Date.now(),
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails()
    };

    if (productId) {
      conversion.productId = productId;
    }

    STATE.metrics.Pixel_Tracking.data.conversions.push(conversion);
    STATE.metrics.Pixel_Tracking.data.type = 'tracking_pixel';
    STATE.metrics.Pixel_Tracking.data.timestamp = Date.now();
    STATE.metrics.Pixel_Tracking.data.page = window.location.pathname;
    STATE.metrics.Pixel_Tracking.data.source = 'webtracking_sdk';
    STATE.metrics.Pixel_Tracking.lastUpdate = Date.now();
    STATE.metrics.Pixel_Tracking.count = STATE.metrics.Pixel_Tracking.data.conversions.length;
  }

  // Track Cookie Operations
  function trackCookieOperation(action, name, value = null, expiryDays = null) {
    if (!STATE.metrics.Cookie_Operations.data.operations) {
      STATE.metrics.Cookie_Operations.data.operations = [];
    }

    const operation = {
      action: action,
      name: name,
      timestamp: Date.now(),
      page: window.location.pathname
    };

    if (value !== null) operation.value = value;
    if (expiryDays !== null) operation.expiryDays = expiryDays;

    STATE.metrics.Cookie_Operations.data.operations.push(operation);

    // Update active cookies list
    if (!STATE.metrics.Cookie_Operations.data.activeCookies) {
      STATE.metrics.Cookie_Operations.data.activeCookies = {};
    }

    if (action === 'set') {
      STATE.metrics.Cookie_Operations.data.activeCookies[name] = value;
    } else if (action === 'delete') {
      delete STATE.metrics.Cookie_Operations.data.activeCookies[name];
    }

    STATE.metrics.Cookie_Operations.lastUpdate = Date.now();
    STATE.metrics.Cookie_Operations.count = STATE.metrics.Cookie_Operations.data.operations.length;
  }

  // Send metrics to API
  async function sendMetrics() {
    if (isConsentMissing()) return;
    if (!CONFIG.domainId) {
      if (CONFIG.debug) console.warn('DomainId not configured');
      return;
    }

    // Update heap tracking before sending
    trackHeapMemory();

    // Update Visitor_ID with customer details
    STATE.metrics.Visitor_ID.data.customerDetails = getCustomerDetails();
    STATE.metrics.Visitor_ID.lastUpdate = Date.now();

    // Update Browser_Fingerprint with customer details
    STATE.metrics.Browser_Fingerprint.data.customerDetails = getCustomerDetails();
    STATE.metrics.Browser_Fingerprint.lastUpdate = Date.now();

    // Update Referrer_Info with customer details
    STATE.metrics.Referrer_Info.data.customerDetails = getCustomerDetails();
    STATE.metrics.Referrer_Info.lastUpdate = Date.now();

    // Update IP_Tracking with customer details
    STATE.metrics.IP_Tracking.data.customerDetails = getCustomerDetails();
    STATE.metrics.IP_Tracking.lastUpdate = Date.now();

    // Update heatmap metadata
    STATE.metrics.Heatmap_Tracking.data.page = window.location.pathname;
    STATE.metrics.Heatmap_Tracking.data.url = window.location.href;
    STATE.metrics.Heatmap_Tracking.data.timestamp = Date.now();
    STATE.metrics.Heatmap_Tracking.data.sessionDuration = Date.now() - STATE.pageLoadTime;
    STATE.metrics.Heatmap_Tracking.data.sessionId = STATE.sessionId;
    STATE.metrics.Heatmap_Tracking.data.visitorId = STATE.visitorId;
    STATE.metrics.Heatmap_Tracking.data.customerDetails = getCustomerDetails();
    STATE.metrics.Heatmap_Tracking.metadata = { pageType: getPageType() };

    const payload = {
      domainId: CONFIG.domainId,
      domainName: CONFIG.domainName,
      visitorId: STATE.visitorId,
      sessionId: STATE.sessionId,
      customerDetails: getCustomerDetails(),
      type: 'metrics',
      trackingData: STATE.metrics
    };

    try {
      await fetch(CONFIG.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      });

      if (CONFIG.debug) console.log('Metrics sent:', payload);
    } catch (error) {
      if (CONFIG.debug) console.error('Failed to send metrics:', error);

      // Fallback to beacon
      if (navigator.sendBeacon) {
        navigator.sendBeacon(CONFIG.apiEndpoint, JSON.stringify(payload));
      }
    }
  }

  // Start batch sending
  function startBatchSending() {
    if (STATE.batchTimer) clearInterval(STATE.batchTimer);

    STATE.batchTimer = setInterval(() => {
      sendMetrics();
    }, CONFIG.batchInterval);
  }

  // Event Listeners
  function attachEventListeners() {
    document.addEventListener('click', function(e) {
      trackClick(e);
      // Detect rage clicks and dead clicks for session recording
      detectRageClick(e);
      detectDeadClick(e);
      if (CONFIG.trackProducts) {
        trackProductClick(e);
      }
    });

    if (CONFIG.trackHeatmap) {
      document.addEventListener('mousemove', trackMouseMove);
      document.addEventListener('scroll', trackScroll);
    }

    // Send metrics before page unload
    window.addEventListener('beforeunload', function() {
      sendMetrics();
    });

    // Track visibility changes
    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'hidden') {
        sendMetrics();
      }
    });
  }

  // Initialize tracking
  function init(config) {
    // Merge config
    Object.assign(CONFIG, config);

    if (!CONFIG.autoTrack) return;
    if (isConsentMissing()) {
      if (CONFIG.debug) console.warn('Consent missing, tracking disabled');
      return;
    }

    // Initialize IDs
    initVisitorId();
    initSessionId();

    // Initialize customer details (Shopify integration)
    initCustomerDetails();

    // Gather initial data
    getBrowserFingerprint();
    getReferrerInfo();
    trackIP();

    // Track initial page view
    trackPageView();
    trackPixelEvent('page_view');

    // Attach listeners
    attachEventListeners();

    // Start batch sending
    startBatchSending();

    if (CONFIG.debug) console.log('Web Tracking SDK initialized', CONFIG);
  }

  // Public API
  window.WebTracking = {
    init: init,
    trackEvent: function(eventName, eventData) {
      // Ensure events array exists
      if (!STATE.metrics.Event_Tracking.data.events) {
        STATE.metrics.Event_Tracking.data.events = [];
      }

      const event = {
        action: eventName,
        timestamp: Date.now(),
        page: window.location.pathname,
        url: window.location.href,
        data: eventData,
        sessionId: STATE.sessionId,
        visitorId: STATE.visitorId,
        customerDetails: getCustomerDetails()
      };

      STATE.metrics.Event_Tracking.data.events.push(event);
      STATE.metrics.Event_Tracking.lastUpdate = Date.now();
      STATE.metrics.Event_Tracking.count = STATE.metrics.Event_Tracking.data.events.length;

      if (CONFIG.debug) console.log('Event tracked:', event);
    },
    setCustomer: function(customerData) {
      updateCustomerDetails(customerData);
    },
    getCustomer: function() {
      return getCustomerDetails();
    },
    sendNow: sendMetrics,
    getVisitorId: function() { return STATE.visitorId; },
    getSessionId: function() { return STATE.sessionId; },
    getMetrics: function() { return STATE.metrics; },
  };

  // Auto-initialize if config exists
  if (window.WebTrackingConfig) {
    init(window.WebTrackingConfig);
  }

})(window, document);
